import { useState, useCallback } from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'

import { getContactByEmail } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetContactByEmailRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { Contact } from '@/gen/cortado/v1/contacts_pb'

interface UseCheckDuplicateContactEmailOptions {
  organizationId: string | null | undefined
  /** Disable the check (e.g., when not in create mode) */
  disabled?: boolean
}

interface UseCheckDuplicateContactEmailResult {
  existingContact: Contact | null
  isChecking: boolean
  /** Call this on blur with the current email value to trigger a check */
  checkEmail: (email: string) => void
  /** Clear the existing contact result */
  clearExistingContact: () => void
}

/**
 * Hook to check if a contact with the given email already exists in the organization.
 * Call `checkEmail` on blur to trigger the duplicate check.
 *
 * @param options.organizationId - The organization to check within
 * @param options.disabled - Whether to disable the check
 * @returns { existingContact, isChecking, checkEmail, clearExistingContact }
 */
export function useCheckDuplicateContactEmail({
  organizationId,
  disabled = false,
}: UseCheckDuplicateContactEmailOptions): UseCheckDuplicateContactEmailResult {
  const [emailToCheck, setEmailToCheck] = useState('')

  // Basic email format validation
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToCheck)

  // Query for existing contact by email
  const query = useQuery(
    getContactByEmail,
    create(GetContactByEmailRequestSchema, {
      organizationId: organizationId || '',
      email: emailToCheck,
    }),
    {
      enabled: !disabled && !!organizationId && isValidEmail && emailToCheck.length > 0,
      retry: false, // Don't retry on 404 (not found is expected)
      staleTime: 30 * 1000, // 30 seconds
    }
  )

  // Call this on blur to trigger the check
  const checkEmail = useCallback((email: string) => {
    const normalized = email.trim().toLowerCase()
    setEmailToCheck(normalized)
  }, [])

  // Clear function to reset state when user takes action
  const clearExistingContact = useCallback(() => {
    setEmailToCheck('')
  }, [])

  return {
    existingContact: query.data?.contact ?? null,
    isChecking: query.isLoading,
    checkEmail,
    clearExistingContact,
  }
}
